import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Plus, Trash2 } from "lucide-react";

// The canonical enum lists, form value type, and empty template live in
// shared/offer-template (pure data — the server-side Build-with-AI persist
// path normalizes against the same source of truth). Re-exported here so
// existing imports from this module keep working unchanged.
import {
  OFFER_TYPES,
  PAIN_INTENSITIES,
  TIME_TO_VALUE,
  PRICING_MODELS,
  DELIVERY_MODELS,
  DELIVERY_LOCATIONS,
  PRIMARY_CTAS,
  SERVICE_TYPES,
  SERVICE_FORMATS,
  type OfferFormValue,
} from "@shared/offer-template";

export {
  OFFER_TYPES,
  PAIN_INTENSITIES,
  TIME_TO_VALUE,
  PRICING_MODELS,
  DELIVERY_MODELS,
  DELIVERY_LOCATIONS,
  PRIMARY_CTAS,
  SERVICE_TYPES,
  SERVICE_FORMATS,
  type OfferFormValue,
};
export { emptyOffer } from "@shared/offer-template";

// Convert array <-> textarea (one per line)
const toLines = (arr: string[] | undefined | null) => Array.isArray(arr) ? arr.join("\n") : "";
const fromLinesRaw = (s: string): string[] => s.split("\n");
const fromLinesClean = (s: string): string[] => s.split("\n").map(l => l.trim()).filter(Boolean);

function ListField({ label, value, onChange, testId, rows = 3 }: { label: string; value: string[] | undefined; onChange: (v: string[]) => void; testId: string; rows?: number; }) {
  const [raw, setRaw] = useState<string>(() => toLines(value));
  const [focused, setFocused] = useState(false);

  // Sync from parent when not actively editing (e.g., async load or external reset).
  useEffect(() => {
    if (!focused) setRaw(toLines(value));
  }, [value, focused]);

  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-medium text-muted-foreground">{label} <span className="text-muted-foreground/60">(one per line)</span></Label>
      <Textarea
        data-testid={testId}
        rows={rows}
        value={raw}
        onFocus={() => setFocused(true)}
        onChange={(e) => {
          setRaw(e.target.value);
          onChange(fromLinesRaw(e.target.value));
        }}
        onBlur={() => {
          setFocused(false);
          const cleaned = fromLinesClean(raw);
          setRaw(cleaned.join("\n"));
          onChange(cleaned);
        }}
      />
    </div>
  );
}

function TextField({ label, value, onChange, testId, placeholder }: { label: string; value: string | undefined | null; onChange: (v: string) => void; testId: string; placeholder?: string; }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-medium text-muted-foreground">{label}</Label>
      <Input data-testid={testId} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
    </div>
  );
}

function AreaField({ label, value, onChange, testId, rows = 2 }: { label: string; value: string | undefined | null; onChange: (v: string) => void; testId: string; rows?: number; }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-medium text-muted-foreground">{label}</Label>
      <Textarea data-testid={testId} rows={rows} value={value || ""} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function SelectField({ label, value, onChange, options, testId }: { label: string; value: string | undefined; onChange: (v: string) => void; options: string[]; testId: string; }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-medium text-muted-foreground">{label}</Label>
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger data-testid={testId}><SelectValue placeholder="Select…" /></SelectTrigger>
        <SelectContent>
          {options.map(o => <SelectItem key={o} value={o}>{o.replace(/_/g, " ")}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

interface Props {
  value: OfferFormValue;
  onChange: (next: OfferFormValue) => void;
}

export function OfferForm({ value, onChange }: Props) {
  const patch = (k: keyof OfferFormValue, v: any) => onChange({ ...value, [k]: v });
  const patchObj = (k: keyof OfferFormValue, subK: string, v: any) => {
    const current = (value[k] as any) || {};
    patch(k, { ...current, [subK]: v });
  };

  const tiers: any[] = Array.isArray(value.offerDetails?.packages_or_tiers) ? value.offerDetails.packages_or_tiers : [];
  const objections: any[] = Array.isArray(value.objections) ? value.objections : [];
  const personaFit: any[] = Array.isArray(value.personaFit) ? value.personaFit : [];

  return (
    <Accordion type="multiple" defaultValue={["basic"]} className="space-y-2">
      <AccordionItem value="basic" className="border rounded-lg bg-card">
        <AccordionTrigger className="px-4" data-testid="section-basic">Basic Info</AccordionTrigger>
        <AccordionContent className="px-4 pb-4 space-y-3">
          <TextField label="Offering name *" value={value.name} onChange={(v) => patch("name", v)} testId="input-offer-name" />
          <div className="grid grid-cols-2 gap-3">
            <SelectField label="Offering type *" value={value.offerType} onChange={(v) => patch("offerType", v)} options={OFFER_TYPES} testId="select-offer-type" />
            <TextField label="Offering category" value={value.offerCategory} onChange={(v) => patch("offerCategory", v)} testId="input-offer-category" placeholder="e.g. business_service" />
          </div>
          <AreaField label="Short description" value={value.shortDescription} onChange={(v) => patch("shortDescription", v)} testId="input-short-description" rows={2} />
          <AreaField label="Summary" value={value.summary} onChange={(v) => patch("summary", v)} testId="input-summary" rows={3} />
        </AccordionContent>
      </AccordionItem>

      <AccordionItem value="buyer_problem" className="border rounded-lg bg-card">
        <AccordionTrigger className="px-4" data-testid="section-buyer-problem">Buyer Problem</AccordionTrigger>
        <AccordionContent className="px-4 pb-4 space-y-3">
          <AreaField label="Primary problem" value={value.buyerProblem?.primary_problem} onChange={(v) => patchObj("buyerProblem", "primary_problem", v)} testId="input-bp-primary" />
          <ListField label="Secondary problems" value={value.buyerProblem?.secondary_problems} onChange={(v) => patchObj("buyerProblem", "secondary_problems", v)} testId="input-bp-secondary" />
          <SelectField label="Pain intensity" value={value.buyerProblem?.pain_intensity} onChange={(v) => patchObj("buyerProblem", "pain_intensity", v)} options={PAIN_INTENSITIES} testId="select-bp-intensity" />
          <ListField label="Current alternatives" value={value.buyerProblem?.current_alternatives} onChange={(v) => patchObj("buyerProblem", "current_alternatives", v)} testId="input-bp-alts" />
          <AreaField label="Status-quo cost" value={value.buyerProblem?.status_quo_cost} onChange={(v) => patchObj("buyerProblem", "status_quo_cost", v)} testId="input-bp-cost" />
        </AccordionContent>
      </AccordionItem>

      <AccordionItem value="desired_outcome" className="border rounded-lg bg-card">
        <AccordionTrigger className="px-4" data-testid="section-desired-outcome">Desired Outcome</AccordionTrigger>
        <AccordionContent className="px-4 pb-4 space-y-3">
          <AreaField label="Primary outcome" value={value.desiredOutcome?.primary_outcome} onChange={(v) => patchObj("desiredOutcome", "primary_outcome", v)} testId="input-do-primary" />
          <ListField label="Secondary outcomes" value={value.desiredOutcome?.secondary_outcomes} onChange={(v) => patchObj("desiredOutcome", "secondary_outcomes", v)} testId="input-do-secondary" />
          <TextField label="Emotional outcome" value={value.desiredOutcome?.emotional_outcome} onChange={(v) => patchObj("desiredOutcome", "emotional_outcome", v)} testId="input-do-emotional" />
          <AreaField label="Business / life impact" value={value.desiredOutcome?.business_or_life_impact} onChange={(v) => patchObj("desiredOutcome", "business_or_life_impact", v)} testId="input-do-impact" />
          <SelectField label="Time to value" value={value.desiredOutcome?.time_to_value} onChange={(v) => patchObj("desiredOutcome", "time_to_value", v)} options={TIME_TO_VALUE} testId="select-do-ttv" />
        </AccordionContent>
      </AccordionItem>

      <AccordionItem value="value_prop" className="border rounded-lg bg-card">
        <AccordionTrigger className="px-4" data-testid="section-value-prop">Value Proposition</AccordionTrigger>
        <AccordionContent className="px-4 pb-4 space-y-3">
          <TextField label="One-liner" value={value.valueProposition?.one_liner} onChange={(v) => patchObj("valueProposition", "one_liner", v)} testId="input-vp-oneliner" />
          <ListField label="Core benefits" value={value.valueProposition?.core_benefits} onChange={(v) => patchObj("valueProposition", "core_benefits", v)} testId="input-vp-benefits" />
          <ListField label="Differentiators" value={value.valueProposition?.differentiators} onChange={(v) => patchObj("valueProposition", "differentiators", v)} testId="input-vp-diff" />
          <TextField label="Why now" value={value.valueProposition?.why_now} onChange={(v) => patchObj("valueProposition", "why_now", v)} testId="input-vp-whynow" />
          <TextField label="Why this brand" value={value.valueProposition?.why_this_brand} onChange={(v) => patchObj("valueProposition", "why_this_brand", v)} testId="input-vp-whybrand" />
        </AccordionContent>
      </AccordionItem>

      <AccordionItem value="offer_details" className="border rounded-lg bg-card">
        <AccordionTrigger className="px-4" data-testid="section-offer-details">Offering Details</AccordionTrigger>
        <AccordionContent className="px-4 pb-4 space-y-3">
          <ListField label="What is included" value={value.offerDetails?.what_is_included} onChange={(v) => patchObj("offerDetails", "what_is_included", v)} testId="input-od-included" />
          <ListField label="What is NOT included" value={value.offerDetails?.what_is_not_included} onChange={(v) => patchObj("offerDetails", "what_is_not_included", v)} testId="input-od-not-included" />
          <SelectField label="Pricing model" value={value.offerDetails?.pricing_model} onChange={(v) => patchObj("offerDetails", "pricing_model", v)} options={PRICING_MODELS} testId="select-od-pricing" />
          <TextField label="Availability" value={value.offerDetails?.availability} onChange={(v) => patchObj("offerDetails", "availability", v)} testId="input-od-availability" />

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-medium text-muted-foreground">Packages / Tiers</Label>
              <Button size="sm" variant="outline" type="button" data-testid="button-add-tier" onClick={() => patchObj("offerDetails", "packages_or_tiers", [...tiers, { tier_name: "", description: "", price_or_price_range: "", best_for: "" }])}>
                <Plus className="h-3 w-3 mr-1" /> Add tier
              </Button>
            </div>
            {tiers.map((t, i) => (
              <Card key={i} className="border-border/40">
                <CardContent className="p-3 space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <Input data-testid={`input-tier-name-${i}`} placeholder="Tier name" value={t.tier_name || ""} onChange={(e) => { const next = [...tiers]; next[i] = { ...t, tier_name: e.target.value }; patchObj("offerDetails", "packages_or_tiers", next); }} />
                    <Button size="icon" variant="ghost" type="button" data-testid={`button-remove-tier-${i}`} onClick={() => { const next = tiers.filter((_, j) => j !== i); patchObj("offerDetails", "packages_or_tiers", next); }}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                  <Textarea rows={2} placeholder="Description" data-testid={`input-tier-desc-${i}`} value={t.description || ""} onChange={(e) => { const next = [...tiers]; next[i] = { ...t, description: e.target.value }; patchObj("offerDetails", "packages_or_tiers", next); }} />
                  <div className="grid grid-cols-2 gap-2">
                    <Input placeholder="Price / range" data-testid={`input-tier-price-${i}`} value={t.price_or_price_range || ""} onChange={(e) => { const next = [...tiers]; next[i] = { ...t, price_or_price_range: e.target.value }; patchObj("offerDetails", "packages_or_tiers", next); }} />
                    <Input placeholder="Best for" data-testid={`input-tier-bestfor-${i}`} value={t.best_for || ""} onChange={(e) => { const next = [...tiers]; next[i] = { ...t, best_for: e.target.value }; patchObj("offerDetails", "packages_or_tiers", next); }} />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </AccordionContent>
      </AccordionItem>

      <AccordionItem value="fulfillment" className="border rounded-lg bg-card">
        <AccordionTrigger className="px-4" data-testid="section-fulfillment">Fulfillment</AccordionTrigger>
        <AccordionContent className="px-4 pb-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <SelectField label="Delivery model" value={value.fulfillment?.delivery_model} onChange={(v) => patchObj("fulfillment", "delivery_model", v)} options={DELIVERY_MODELS} testId="select-ff-model" />
            <SelectField label="Delivery location" value={value.fulfillment?.delivery_location} onChange={(v) => patchObj("fulfillment", "delivery_location", v)} options={DELIVERY_LOCATIONS} testId="select-ff-loc" />
          </div>
          <TextField label="Timeline" value={value.fulfillment?.timeline} onChange={(v) => patchObj("fulfillment", "timeline", v)} testId="input-ff-timeline" />
          <ListField label="Process steps" value={value.fulfillment?.process_steps} onChange={(v) => patchObj("fulfillment", "process_steps", v)} testId="input-ff-steps" />
          <ListField label="Customer requirements" value={value.fulfillment?.customer_requirements} onChange={(v) => patchObj("fulfillment", "customer_requirements", v)} testId="input-ff-reqs" />
          <ListField label="Onboarding steps" value={value.fulfillment?.onboarding_steps} onChange={(v) => patchObj("fulfillment", "onboarding_steps", v)} testId="input-ff-onboarding" />
        </AccordionContent>
      </AccordionItem>

      <AccordionItem value="proof" className="border rounded-lg bg-card">
        <AccordionTrigger className="px-4" data-testid="section-proof">Proof</AccordionTrigger>
        <AccordionContent className="px-4 pb-4 space-y-3">
          <ListField label="Case studies" value={value.proof?.case_studies} onChange={(v) => patchObj("proof", "case_studies", v)} testId="input-pf-cases" />
          <ListField label="Testimonials" value={value.proof?.testimonials} onChange={(v) => patchObj("proof", "testimonials", v)} testId="input-pf-testi" />
          <ListField label="Credentials" value={value.proof?.credentials} onChange={(v) => patchObj("proof", "credentials", v)} testId="input-pf-creds" />
          <ListField label="Metrics" value={value.proof?.metrics} onChange={(v) => patchObj("proof", "metrics", v)} testId="input-pf-metrics" />
          <ListField label="Before/after examples" value={value.proof?.before_after_examples} onChange={(v) => patchObj("proof", "before_after_examples", v)} testId="input-pf-ba" />
          <ListField label="Trust signals" value={value.proof?.trust_signals} onChange={(v) => patchObj("proof", "trust_signals", v)} testId="input-pf-trust" />
        </AccordionContent>
      </AccordionItem>

      <AccordionItem value="risk" className="border rounded-lg bg-card">
        <AccordionTrigger className="px-4" data-testid="section-risk">Risk Reversal</AccordionTrigger>
        <AccordionContent className="px-4 pb-4 space-y-3">
          <TextField label="Guarantee" value={value.riskReversal?.guarantee} onChange={(v) => patchObj("riskReversal", "guarantee", v)} testId="input-rr-guarantee" />
          <TextField label="Refund policy" value={value.riskReversal?.refund_policy} onChange={(v) => patchObj("riskReversal", "refund_policy", v)} testId="input-rr-refund" />
          <TextField label="Contract terms" value={value.riskReversal?.contract_terms} onChange={(v) => patchObj("riskReversal", "contract_terms", v)} testId="input-rr-contract" />
          <TextField label="Trial / intro offer" value={value.riskReversal?.trial_or_intro_offer} onChange={(v) => patchObj("riskReversal", "trial_or_intro_offer", v)} testId="input-rr-trial" />
        </AccordionContent>
      </AccordionItem>

      <AccordionItem value="objections" className="border rounded-lg bg-card">
        <AccordionTrigger className="px-4" data-testid="section-objections">Objections</AccordionTrigger>
        <AccordionContent className="px-4 pb-4 space-y-3">
          <div className="flex items-center justify-end">
            <Button size="sm" variant="outline" type="button" data-testid="button-add-objection" onClick={() => patch("objections", [...objections, { objection: "", response: "", proof_to_use: "" }])}>
              <Plus className="h-3 w-3 mr-1" /> Add objection
            </Button>
          </div>
          {objections.map((o, i) => (
            <Card key={i} className="border-border/40">
              <CardContent className="p-3 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <Input placeholder="Objection" data-testid={`input-obj-text-${i}`} value={o.objection || ""} onChange={(e) => { const next = [...objections]; next[i] = { ...o, objection: e.target.value }; patch("objections", next); }} />
                  <Button size="icon" variant="ghost" type="button" data-testid={`button-remove-objection-${i}`} onClick={() => patch("objections", objections.filter((_, j) => j !== i))}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
                <Textarea rows={2} placeholder="Response" data-testid={`input-obj-resp-${i}`} value={o.response || ""} onChange={(e) => { const next = [...objections]; next[i] = { ...o, response: e.target.value }; patch("objections", next); }} />
                <Input placeholder="Proof to use" data-testid={`input-obj-proof-${i}`} value={o.proof_to_use || ""} onChange={(e) => { const next = [...objections]; next[i] = { ...o, proof_to_use: e.target.value }; patch("objections", next); }} />
              </CardContent>
            </Card>
          ))}
        </AccordionContent>
      </AccordionItem>

      <AccordionItem value="conversion" className="border rounded-lg bg-card">
        <AccordionTrigger className="px-4" data-testid="section-conversion">Conversion</AccordionTrigger>
        <AccordionContent className="px-4 pb-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <SelectField label="Primary CTA" value={value.conversion?.primary_cta} onChange={(v) => patchObj("conversion", "primary_cta", v)} options={PRIMARY_CTAS} testId="select-cv-cta" />
            <TextField label="CTA copy" value={value.conversion?.cta_copy} onChange={(v) => patchObj("conversion", "cta_copy", v)} testId="input-cv-copy" />
          </div>
          <TextField label="Conversion URL" value={value.conversion?.conversion_url} onChange={(v) => patchObj("conversion", "conversion_url", v)} testId="input-cv-url" />
          <ListField label="Lead-capture fields" value={value.conversion?.lead_capture_fields} onChange={(v) => patchObj("conversion", "lead_capture_fields", v)} testId="input-cv-fields" />
          <ListField label="Qualification criteria" value={value.conversion?.qualification_criteria} onChange={(v) => patchObj("conversion", "qualification_criteria", v)} testId="input-cv-quals" />
        </AccordionContent>
      </AccordionItem>

      <AccordionItem value="persona_fit" className="border rounded-lg bg-card">
        <AccordionTrigger className="px-4" data-testid="section-persona-fit">Persona Fit</AccordionTrigger>
        <AccordionContent className="px-4 pb-4 space-y-3">
          <div className="flex items-center justify-end">
            <Button size="sm" variant="outline" type="button" data-testid="button-add-persona-fit" onClick={() => patch("personaFit", [...personaFit, { persona_name: "", why_they_need_it: "", main_pain: "", main_desire: "", likely_objection: "", best_angle: "" }])}>
              <Plus className="h-3 w-3 mr-1" /> Add persona fit
            </Button>
          </div>
          {personaFit.map((p, i) => (
            <Card key={i} className="border-border/40">
              <CardContent className="p-3 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <Input placeholder="Persona name" data-testid={`input-pf-name-${i}`} value={p.persona_name || ""} onChange={(e) => { const next = [...personaFit]; next[i] = { ...p, persona_name: e.target.value }; patch("personaFit", next); }} />
                  <Button size="icon" variant="ghost" type="button" data-testid={`button-remove-pf-${i}`} onClick={() => patch("personaFit", personaFit.filter((_, j) => j !== i))}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
                <Textarea rows={2} placeholder="Why they need it" data-testid={`input-pf-why-${i}`} value={p.why_they_need_it || ""} onChange={(e) => { const next = [...personaFit]; next[i] = { ...p, why_they_need_it: e.target.value }; patch("personaFit", next); }} />
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder="Main pain" data-testid={`input-pf-pain-${i}`} value={p.main_pain || ""} onChange={(e) => { const next = [...personaFit]; next[i] = { ...p, main_pain: e.target.value }; patch("personaFit", next); }} />
                  <Input placeholder="Main desire" data-testid={`input-pf-desire-${i}`} value={p.main_desire || ""} onChange={(e) => { const next = [...personaFit]; next[i] = { ...p, main_desire: e.target.value }; patch("personaFit", next); }} />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder="Likely objection" data-testid={`input-pf-obj-${i}`} value={p.likely_objection || ""} onChange={(e) => { const next = [...personaFit]; next[i] = { ...p, likely_objection: e.target.value }; patch("personaFit", next); }} />
                  <Input placeholder="Best angle" data-testid={`input-pf-angle-${i}`} value={p.best_angle || ""} onChange={(e) => { const next = [...personaFit]; next[i] = { ...p, best_angle: e.target.value }; patch("personaFit", next); }} />
                </div>
              </CardContent>
            </Card>
          ))}
        </AccordionContent>
      </AccordionItem>

      <AccordionItem value="creative_inputs" className="border rounded-lg bg-card">
        <AccordionTrigger className="px-4" data-testid="section-creative-inputs">Creative Inputs</AccordionTrigger>
        <AccordionContent className="px-4 pb-4 space-y-3">
          <ListField label="Hooks" value={value.creativeInputs?.hooks} onChange={(v) => patchObj("creativeInputs", "hooks", v)} testId="input-ci-hooks" rows={4} />
          <ListField label="Angles" value={value.creativeInputs?.angles} onChange={(v) => patchObj("creativeInputs", "angles", v)} testId="input-ci-angles" />
          <ListField label="Claims" value={value.creativeInputs?.claims} onChange={(v) => patchObj("creativeInputs", "claims", v)} testId="input-ci-claims" />
          <ListField label="Forbidden claims" value={value.creativeInputs?.forbidden_claims} onChange={(v) => patchObj("creativeInputs", "forbidden_claims", v)} testId="input-ci-forbidden" />
          <TextField label="Tone" value={value.creativeInputs?.tone} onChange={(v) => patchObj("creativeInputs", "tone", v)} testId="input-ci-tone" />
          <ListField label="Visual direction" value={value.creativeInputs?.visual_direction} onChange={(v) => patchObj("creativeInputs", "visual_direction", v)} testId="input-ci-visual" />
          <ListField label="UGC scenarios" value={value.creativeInputs?.ugc_scenarios} onChange={(v) => patchObj("creativeInputs", "ugc_scenarios", v)} testId="input-ci-ugc" />
          <ListField label="Landing-page messages" value={value.creativeInputs?.landing_page_messages} onChange={(v) => patchObj("creativeInputs", "landing_page_messages", v)} testId="input-ci-lp" />
        </AccordionContent>
      </AccordionItem>

      <AccordionItem value="service_details" className="border rounded-lg bg-card">
        <AccordionTrigger className="px-4" data-testid="section-service-details">Service Details</AccordionTrigger>
        <AccordionContent className="px-4 pb-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <SelectField label="Service type" value={value.serviceDetails?.service_type} onChange={(v) => patchObj("serviceDetails", "service_type", v)} options={SERVICE_TYPES} testId="select-sd-type" />
            <SelectField label="Service format" value={value.serviceDetails?.service_format} onChange={(v) => patchObj("serviceDetails", "service_format", v)} options={SERVICE_FORMATS} testId="select-sd-format" />
          </div>
          <AreaField label="Service scope" value={value.serviceDetails?.service_scope} onChange={(v) => patchObj("serviceDetails", "service_scope", v)} testId="input-sd-scope" />
          <ListField label="Deliverables" value={value.serviceDetails?.deliverables} onChange={(v) => patchObj("serviceDetails", "deliverables", v)} testId="input-sd-deliverables" />
          <ListField label="Client inputs needed" value={value.serviceDetails?.client_inputs_needed} onChange={(v) => patchObj("serviceDetails", "client_inputs_needed", v)} testId="input-sd-client-inputs" />
          <ListField label="Provider actions" value={value.serviceDetails?.provider_actions} onChange={(v) => patchObj("serviceDetails", "provider_actions", v)} testId="input-sd-provider-actions" />
          <ListField label="Client actions" value={value.serviceDetails?.client_actions} onChange={(v) => patchObj("serviceDetails", "client_actions", v)} testId="input-sd-client-actions" />
          <AreaField label="Success definition" value={value.serviceDetails?.success_definition} onChange={(v) => patchObj("serviceDetails", "success_definition", v)} testId="input-sd-success" />
          <div className="grid grid-cols-2 gap-3">
            <TextField label="Service area" value={value.serviceDetails?.service_area} onChange={(v) => patchObj("serviceDetails", "service_area", v)} testId="input-sd-area" />
            <TextField label="Capacity limits" value={value.serviceDetails?.capacity_limits} onChange={(v) => patchObj("serviceDetails", "capacity_limits", v)} testId="input-sd-capacity" />
          </div>
          <ListField label="Booking / sales process" value={value.serviceDetails?.booking_or_sales_process} onChange={(v) => patchObj("serviceDetails", "booking_or_sales_process", v)} testId="input-sd-booking" />
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}
